import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  Button,
} from "@mui/material";
import { Alert } from "@mui/material";
import { useAllLocationsData } from "../../hooks";
import {
  getLocationsDropdown,
  getYardHeats,
  getGrades,
  getSections,
  getLengths,
  getBilletQualityStatuses,
} from "../../repository/BilletYardInventoryRepository";
import {
  SelectionColumn,
  SectionColumn,
  GradeColumn,
  ActionButtonsSection,
  QualityDestinationColumn,
} from "./shared/components";
import type { ResponseMESF } from "@dexteel/mesf-core";
import { SearchResults } from "../../models";
import {
  DropdownItem,
  filterUnknownOnly,
} from "./search-modal/utils/searchDataUtils";

interface SearchCriteria {
  locations: string[];
  heats: string[];
  sections: string[];
  lengths: string[];
  grades: string[];
  qualities: string[];
  destinations: string[];
}

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  onSearchResults: (results: SearchResults | null) => void;
  initialCriteria?: SearchCriteria;
  onCriteriaUpdate?: (criteria: SearchCriteria) => void;
}

// Feature flag for multi-selection (set to false to match old system behavior)
const ALLOW_MULTIPLE_SELECTION = false;

// Destination options - only Customer (BIC) and Roanoke (BIR)
const destinationOptions = ["Customer", "Roanoke"];

export const SearchModal = ({
  open,
  onClose,
  onSearchResults,
  initialCriteria,
  onCriteriaUpdate,
}: SearchModalProps) => {
  // Track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  // Search-specific state
  const [isSearching, setIsSearching] = useState(false);
  const [operationError, setOperationError] = useState<string>("");
  const [hasSearched, setHasSearched] = useState(false);

  // Search criteria state - initialize with parent's criteria
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    initialCriteria?.locations || [],
  );
  const [selectedHeats, setSelectedHeats] = useState<string[]>(
    initialCriteria?.heats || [],
  );
  const [selectedSections, setSelectedSections] = useState<string[]>(
    initialCriteria?.sections || [],
  );
  const [selectedLengths, setSelectedLengths] = useState<string[]>(
    initialCriteria?.lengths || [],
  );
  const [selectedGrades, setSelectedGrades] = useState<string[]>(
    initialCriteria?.grades || [],
  );
  const [selectedQualities, setSelectedQualities] = useState<string[]>(
    initialCriteria?.qualities || [],
  );
  const [selectedDestinations, setSelectedDestinations] = useState<string[]>(
    initialCriteria?.destinations || [],
  );

  // Dropdown data state
  const [locationsData, setLocationsData] = useState<DropdownItem[]>([]);
  const [heatsData, setHeatsData] = useState<DropdownItem[]>([]);
  const [gradesData, setGradesData] = useState<DropdownItem[]>([]);
  const [sectionsData, setSectionsData] = useState<DropdownItem[]>([]);
  const [lengthsData, setLengthsData] = useState<DropdownItem[]>([]);
  const [qualitiesData, setQualitiesData] = useState<DropdownItem[]>([]);

  // Loading states
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Get current locations data for search results
  const {
    locations,
    specialLocations,
    groupsByLocationId,
    isLoading,
    isRefreshing,
    error,
    fetchData,
  } = useAllLocationsData();

  // Load dropdown data when modal opens
  useEffect(() => {
    if (open) {
      loadDropdownData();
    }
  }, [open]);

  // Cleanup effect to track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadDropdownData = async () => {
    setIsLoadingData(true);
    setOperationError("");

    try {
      const extractRows = (resp: ResponseMESF) =>
        resp.ok ? resp.data?.tables?.[0]?.rows || [] : [];
      const [
        locationsResult,
        heatsResult,
        gradesResult,
        sectionsResult,
        lengthsResult,
        qualitiesResult,
      ] = await Promise.all([
        getLocationsDropdown(),
        getYardHeats(),
        getGrades(),
        getSections(),
        getLengths(),
        getBilletQualityStatuses(),
      ]);

      // Extract data from API responses and filter out only "Unknown" values
      if (isMountedRef.current) {
        setLocationsData(
          filterUnknownOnly(extractRows(locationsResult), "Locations"),
        );
        setHeatsData(filterUnknownOnly(extractRows(heatsResult), "Heats"));
        setGradesData(filterUnknownOnly(extractRows(gradesResult), "Grades"));
        setSectionsData(
          filterUnknownOnly(extractRows(sectionsResult), "Sections"),
        );
        setLengthsData(
          filterUnknownOnly(extractRows(lengthsResult), "Lengths"),
        );
        setQualitiesData(
          filterUnknownOnly(extractRows(qualitiesResult), "Qualities"),
        );
      }
    } catch (error: unknown) {
      /* Error handled silently */
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setOperationError("Failed to load search filter data");
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsLoadingData(false);
      }
    }
  };

  // Chip toggle handlers
  const handleChipToggle = (
    value: string,
    selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    const isSelected = selectedItems.includes(value);

    if (ALLOW_MULTIPLE_SELECTION) {
      // Multi-selection mode (preserved for future use)
      if (isSelected) {
        setSelectedItems(selectedItems.filter((item) => item !== value));
      } else {
        setSelectedItems([...selectedItems, value]);
      }
    } else {
      // Single-selection mode (matches old system behavior)
      if (isSelected) {
        // Deselect if clicking the same item
        setSelectedItems([]);
      } else {
        // Select only this item (replace any previous selection)
        setSelectedItems([value]);
      }
    }
  };

  // Search filtering logic moved to useSearchIntegration hook

  // Search function - updates parent criteria (parent handles the actual search)
  const handleSearch = useCallback(async () => {
    if (!onCriteriaUpdate) return;

    setIsSearching(true);
    setHasSearched(true);
    setOperationError("");

    try {
      // Create search criteria object
      const searchCriteria: SearchCriteria = {
        locations: selectedLocations,
        heats: selectedHeats,
        sections: selectedSections,
        lengths: selectedLengths,
        grades: selectedGrades,
        qualities: selectedQualities,
        destinations: selectedDestinations,
      };

      // Update parent's search criteria (this will trigger automatic search)
      onCriteriaUpdate(searchCriteria);

      // Create a dummy result for the onSearchResults callback (needed for modal close logic)
      const dummyResults: SearchResults = {
        locationIds: [], // Will be filled by parent's automatic search
        groupIds: [], // Will be filled by parent's automatic search
        searchCriteria: searchCriteria,
      };

      // Notify parent that search was triggered
      onSearchResults(dummyResults);

      // Auto-close modal after search (like old system behavior)
      onClose();
    } catch (error: unknown) {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setOperationError(
          "Search failed: " +
            (error instanceof Error ? error.message : "Unknown error"),
        );
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsSearching(false);
      }
    }
  }, [
    selectedLocations,
    selectedHeats,
    selectedSections,
    selectedLengths,
    selectedGrades,
    selectedQualities,
    selectedDestinations,
    onCriteriaUpdate,
    onSearchResults,
    onClose,
  ]);

  // Check if any search criteria is selected
  const hasAnySelection = useMemo(() => {
    return (
      selectedLocations.length > 0 ||
      selectedHeats.length > 0 ||
      selectedSections.length > 0 ||
      selectedLengths.length > 0 ||
      selectedGrades.length > 0 ||
      selectedQualities.length > 0 ||
      selectedDestinations.length > 0
    );
  }, [
    selectedLocations,
    selectedHeats,
    selectedSections,
    selectedLengths,
    selectedGrades,
    selectedQualities,
    selectedDestinations,
  ]);

  // Reset function (doesn't close modal)
  const handleReset = () => {
    setSelectedLocations([]);
    setSelectedHeats([]);
    setSelectedSections([]);
    setSelectedLengths([]);
    setSelectedGrades([]);
    setSelectedQualities([]);
    setSelectedDestinations([]);
    setHasSearched(false);
    setOperationError("");

    // Clear search results in parent by updating criteria to empty
    if (onCriteriaUpdate) {
      const emptyCriteria: SearchCriteria = {
        locations: [],
        heats: [],
        sections: [],
        lengths: [],
        grades: [],
        qualities: [],
        destinations: [],
      };
      onCriteriaUpdate(emptyCriteria);
    }
  };

  const handleClose = () => {
    if (isSearching) return; // Prevent closing during search
    onClose();
  };

  if (error) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "90vw",
            width: 1200,
            maxHeight: "90vh",
            margin: (theme) => theme.spacing(1),
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            borderBottom: (theme) =>
              `1px solid ${theme.palette.billetYard.borders.medium}`,
            padding: "12px 24px",
          }}
        >
          Error
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="error"
            sx={{
              margin: (theme) => theme.spacing(2),
            }}
          >
            Failed to load data: {error}
          </Alert>
        </DialogContent>
        <DialogActions>
          <ActionButtonsSection onClose={handleClose} />
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      disableEscapeKeyDown={isSearching}
    >
      <DialogTitle
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          borderBottom: (theme) =>
            `1px solid ${theme.palette.billetYard.borders.medium}`,
          padding: "12px 24px",
        }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontWeight: "bold",
            color: "white",
            textAlign: "center",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Search Billets
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          height: "calc(100vh - 120px)",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {isLoadingData ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "2rem",
              flex: 1,
            }}
          >
            <CircularProgress />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ marginTop: "1rem" }}
            >
              Loading search filters...
            </Typography>
          </Box>
        ) : (
          <Box
            display="flex"
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              border: "none",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              paddingTop: "16px",
            }}
          >
            {/* Column 1: Location */}
            <SelectionColumn
              title="Location"
              data={locationsData}
              selectedItems={selectedLocations}
              onSelectionChange={(value) =>
                handleChipToggle(value, selectedLocations, setSelectedLocations)
              }
              valueField="Value"
              titleField="Title"
              layoutMode="dynamic"
              isNarrowColumn={false}
              customWidth="15%"
            />

            {/* Column 2: Heat */}
            <SelectionColumn
              title="Heat"
              data={heatsData}
              selectedItems={selectedHeats}
              onSelectionChange={(value) =>
                handleChipToggle(value, selectedHeats, setSelectedHeats)
              }
              valueField="Value"
              titleField="Title"
              layoutMode="dynamic"
              isNarrowColumn={false}
              customWidth="15%"
            />

            {/* Column 3: Section */}
            <SectionColumn
              sections={sectionsData}
              selectedSections={selectedSections}
              onSectionChange={(value) =>
                handleChipToggle(value, selectedSections, setSelectedSections)
              }
              customWidth="15%"
            />

            {/* Column 4: Length (two columns of data) */}
            <SelectionColumn
              title="Length"
              data={lengthsData}
              selectedItems={selectedLengths}
              onSelectionChange={(value) =>
                handleChipToggle(value, selectedLengths, setSelectedLengths)
              }
              valueField="Value"
              titleField="Title"
              layoutMode="twoPerRow"
              isNarrowColumn={false}
              customWidth="15%"
            />

            {/* Column 5: Grade (uses rest of width) */}
            <GradeColumn
              grades={gradesData}
              selectedGrades={selectedGrades}
              onGradeChange={(value) =>
                handleChipToggle(value, selectedGrades, setSelectedGrades)
              }
              customWidth="45%"
            />

            {/* Column 6: Quality & Destination stacked */}
            <QualityDestinationColumn
              qualities={qualitiesData}
              selectedQualities={selectedQualities}
              onQualityChange={(value) =>
                handleChipToggle(value, selectedQualities, setSelectedQualities)
              }
              destinations={destinationOptions.map((dest) => ({
                value: dest,
                label: dest,
              }))}
              selectedDestinations={selectedDestinations}
              onDestinationChange={(value) => {
                if (value !== null) {
                  handleChipToggle(
                    value,
                    selectedDestinations,
                    setSelectedDestinations,
                  );
                }
              }}
              customWidth="15%"
              showTitleDivider={true}
            />
          </Box>
        )}

        {operationError && (
          <Alert
            severity="error"
            sx={{
              margin: (theme) => theme.spacing(2),
            }}
          >
            {operationError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{ padding: "24px", justifyContent: "center", gap: "16px" }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={isSearching}
          sx={{
            minWidth: "200px",
            minHeight: "64px",
            fontSize: "20px",
            fontWeight: "bold",
            padding: "16px 48px",
            borderRadius: "8px",
            boxShadow: "0 4px 14px 0 rgba(63, 81, 181, 0.25)",
          }}
        >
          Search
        </Button>

        {/* RESET button - always visible, disabled when no selections */}
        <Button
          variant="contained"
          color="inherit"
          onClick={handleReset}
          disabled={isSearching || !hasAnySelection}
          sx={{
            minWidth: "200px",
            minHeight: "64px",
            fontSize: "20px",
            fontWeight: "bold",
            padding: "16px 48px",
            borderRadius: "8px",
            boxShadow: "0 4px 14px 0 rgba(158, 158, 158, 0.25)",
          }}
        >
          Reset
        </Button>

        <Button
          variant="contained"
          onClick={handleClose}
          disabled={isSearching}
          sx={{
            minWidth: "200px",
            minHeight: "64px",
            fontSize: "20px",
            fontWeight: "bold",
            padding: "16px 48px",
            borderRadius: "8px",
            backgroundColor: (theme) =>
              theme.palette.billetYard.backgrounds.light,
            color: (theme) => theme.palette.text.primary,
            border: (theme) =>
              `1px solid ${theme.palette.billetYard.borders.dark}`,
            boxShadow: "0 4px 14px 0 rgba(158, 158, 158, 0.25)",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
